import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { 
  FiPackage, 
  FiDollarSign, 
  FiCalendar, 
  FiStar, 
  FiPlus, 
  FiX, 
  FiLoader, 
  FiCheck,
  FiFileText,
  FiAward,
  FiTag
} from "react-icons/fi";
import { RiCoinsLine } from "react-icons/ri";

const CreatePlan = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState([""]);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    coins: "",
    bonusCoins: "",
    price: "",
    validity: "30",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...features];
    updatedFeatures[index] = value;
    setFeatures(updatedFeatures);
  };

  const addFeature = () => {
    setFeatures([...features, ""]);
  };

  const removeFeature = (index) => {
    if (features.length > 1) {
      const updatedFeatures = features.filter((_, i) => i !== index);
      setFeatures(updatedFeatures);
    } else {
      toast.error("At least one feature is required");
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error("Plan title is required");
      return false;
    }
    if (!formData.coins || formData.coins <= 0) {
      toast.error("Valid coins amount is required");
      return false;
    }
    if (!formData.price || formData.price <= 0) {
      toast.error("Valid price is required");
      return false;
    }
    
    // Filter out empty features
    const nonEmptyFeatures = features.filter(f => f.trim() !== "");
    if (nonEmptyFeatures.length === 0) {
      toast.error("At least one feature is required");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "https://api.dreamtoday.in";
      
      // Filter out empty features
      const nonEmptyFeatures = features.filter(f => f.trim() !== "");
      
      const planData = {
        title: formData.title,
        description: formData.description,
        coins: Number(formData.coins),
        bonusCoins: Number(formData.bonusCoins) || 0,
        price: Number(formData.price),
        features: nonEmptyFeatures,
        validity: Number(formData.validity) || 30
      };

      const response = await axios.post(
        `${apiUrl}/api/admin/createplan`,
        planData,
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (response.data.success) {
        toast.success("Plan created successfully!");
        // Navigate to all plans page
        navigate("/planlist");
      }
    } catch (error) {
      console.error("Error creating plan:", error);
      toast.error(error.response?.data?.message || "Failed to create plan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiPackage className="text-blue-500" />
            Create New Plan
          </h1>
          <p className="text-gray-600 mt-2 text-sm md:text-base">
            Fill in the details below to create a new subscription plan
          </p>
        </div>

        {/* Main Form */}
        <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-8">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 md:mb-6 flex items-center gap-2">
              <FiFileText className="text-blue-500" />
              Basic Information
            </h2>
            
            <div className="space-y-4 md:space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiTag className="text-green-500" />
                    Plan Title *
                  </div>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  placeholder="e.g., Basic Plan, Premium Plan, Gold Plan"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiFileText className="text-gray-500" />
                    Description
                  </div>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  placeholder="Enter plan description (optional)"
                />
              </div>
            </div>
          </div>

          {/* Coins & Price */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-8">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 md:mb-6 flex items-center gap-2">
              <RiCoinsLine className="text-yellow-500" />
              Coins & Pricing
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Coins */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <RiCoinsLine className="text-yellow-500" />
                    Coins *
                  </div>
                </label>
                <input
                  type="number"
                  name="coins"
                  value={formData.coins}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  placeholder="Enter number of coins"
                  min="1"
                  required
                />
              </div>

              {/* Bonus Coins */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiAward className="text-purple-500" />
                    Bonus Coins
                  </div>
                </label>
                <input
                  type="number"
                  name="bonusCoins"
                  value={formData.bonusCoins}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  placeholder="Enter bonus coins (optional)"
                  min="0"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiDollarSign className="text-green-500" />
                    Price (₹) *
                  </div>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                  placeholder="Enter price"
                  min="1"
                  required
                />
              </div>

              {/* Validity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-1">
                    <FiCalendar className="text-blue-500" />
                    Validity (Days)
                  </div>
                </label>
                <select
                  name="validity"
                  value={formData.validity}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                >
                  <option value="7">7 Days</option>
                  <option value="15">15 Days</option>
                  <option value="30">30 Days (1 Month)</option>
                  <option value="60">60 Days (2 Months)</option>
                  <option value="90">90 Days (3 Months)</option>
                  <option value="180">180 Days (6 Months)</option>
                  <option value="365">365 Days (1 Year)</option>
                  <option value="0">Lifetime</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select 0 for lifetime validity
                </p>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 md:mb-6">
              <h2 className="text-lg md:text-xl font-semibold text-gray-800 flex items-center gap-2">
                <FiStar className="text-yellow-500" />
                Plan Features
              </h2>
              <button
                type="button"
                onClick={addFeature}
                className="w-full sm:w-auto px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center justify-center gap-2 text-sm md:text-base"
              >
                <FiPlus />
                Add Feature
              </button>
            </div>
            
            <div className="space-y-3 md:space-y-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 md:gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                      placeholder={`Feature ${index + 1} (e.g., 100 Coins per day)`}
                    />
                  </div>
                  {features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <FiX className="text-lg md:text-xl" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Feature Tips */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs md:text-sm text-blue-700 flex items-center gap-2">
                <FiStar className="text-blue-500" />
                Example features: "100 Coins daily", "Priority support", "No ads", "Exclusive content"
              </p>
            </div>
          </div>

          {/* Summary Card */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 text-white">
            <h3 className="text-base md:text-lg font-semibold mb-3 flex items-center gap-2">
              <FiPackage />
              Plan Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 text-sm">
              <div>
                <p className="text-blue-100">Plan</p>
                <p className="font-semibold truncate">{formData.title || "Not set"}</p>
              </div>
              <div>
                <p className="text-blue-100">Coins</p>
                <p className="font-semibold">{formData.coins || "0"}</p>
              </div>
              <div>
                <p className="text-blue-100">Bonus</p>
                <p className="font-semibold">{formData.bonusCoins || "0"}</p>
              </div>
              <div>
                <p className="text-blue-100">Price</p>
                <p className="font-semibold">₹{formData.price || "0"}</p>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => navigate("/plan/all")}
              className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm md:text-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-6 md:px-8 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm md:text-base"
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FiCheck />
                  Create Plan
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePlan;